const INTERNAL_FIELDS = new Set(['_id', '__v'])

export function catalogId(value) {
  if (value === null || value === undefined) return ''
  if (typeof value === 'string' || typeof value === 'number') return String(value)
  if (typeof value === 'object') {
    if ('$oid' in value) return catalogId(value.$oid)
    if ('id' in value) return catalogId(value.id)
    if ('_id' in value) return catalogId(value._id)
  }
  return ''
}

function normalizeValue(value) {
  if (Array.isArray(value)) return value.map(normalizeValue)
  if (!value || typeof value !== 'object') return value
  if ('$oid' in value) return catalogId(value)
  if ('$numberDecimal' in value) return String(value.$numberDecimal)
  if ('$date' in value) return String(value.$date)

  const normalized = Object.entries(value).reduce((result, [key, nested]) => {
    if (!INTERNAL_FIELDS.has(key) && !key.startsWith('__')) result[key] = normalizeValue(nested)
    return result
  }, {})
  const nestedId = catalogId(value._id)
  if (nestedId && !normalized.id) normalized.id = nestedId
  return normalized
}

export function normalizeCatalogEntity(raw = {}) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {}
  const normalized = normalizeValue(raw)
  const id = catalogId(raw.id ?? raw._id)
  delete normalized._id
  if (id) normalized.id = id
  return normalized
}

export function normalizeListEnvelope(raw = {}) {
  return {
    items: Array.isArray(raw?.items) ? raw.items.map(normalizeCatalogEntity) : [],
    count: Number(raw?.count) || 0,
  }
}

export function resolveTranslatedField(translations, locale, field) {
  const requested = translations?.[locale]?.[field]
  if (typeof requested === 'string' && requested.trim() !== '') return requested
  const ukrainian = translations?.uk?.[field]
  return typeof ukrainian === 'string' && ukrainian.trim() !== '' ? ukrainian : ''
}

export function mergeVisibleTranslations(original = {}, visible = {}) {
  const merged = structuredCloneSafe(original)
  for (const locale of ['uk', 'en']) {
    if (!visible[locale]) continue
    merged[locale] = { ...(merged[locale] || {}), ...visible[locale] }
  }
  return merged
}

function structuredCloneSafe(value) {
  return JSON.parse(JSON.stringify(value || {}))
}

export function buildEntityTranslationPatch(original = {}, visible = {}) {
  const patch = {}
  for (const locale of ['uk', 'en']) {
    for (const field of ['name', 'description']) {
      if (!Object.prototype.hasOwnProperty.call(visible?.[locale] || {}, field)) continue
      const next = visible[locale][field] ?? ''
      const previous = original?.[locale]?.[field] ?? undefined
      if (locale === 'en' && field === 'name' && String(next).trim() === '') continue
      if (next !== previous) {
        patch[locale] ||= {}
        patch[locale][field] = next
      }
    }
  }
  return patch
}

export function buildCreateEntityTranslations(visible = {}) {
  const ukName = String(visible?.uk?.name ?? '').trim()
  if (!ukName) throw new Error('Українська назва є обов’язковою.')
  const result = {
    uk: {
      name: ukName,
      description: String(visible?.uk?.description ?? '').trim(),
    },
  }
  const enName = String(visible?.en?.name ?? '').trim()
  const enDescription = String(visible?.en?.description ?? '').trim()
  if (enName || enDescription) {
    result.en = {}
    if (enName) result.en.name = enName
    if (enDescription) result.en.description = enDescription
  }
  return result
}

export function visibleEntityTranslations(translations = {}) {
  return {
    uk: {
      name: translations?.uk?.name ?? '',
      description: translations?.uk?.description ?? '',
    },
    en: {
      name: translations?.en?.name ?? '',
      description: translations?.en?.description ?? '',
    },
  }
}

export function buildValueTranslationPayload(values = {}, changedLocales = ['uk', 'en']) {
  const uk = String(values.uk ?? '').trim()
  if (!uk) throw new Error('Потрібне непорожнє українське значення.')
  const locales = new Set(changedLocales)
  locales.add('uk')
  const translations = {}
  for (const locale of locales) {
    translations[locale] = { value: String(values[locale] ?? '').trim() }
  }
  return translations
}

export function isRelativeMediaPath(path) {
  return typeof path === 'string'
    && /^[A-Za-z0-9][A-Za-z0-9._-]*(?:\/[A-Za-z0-9][A-Za-z0-9._-]*)*$/.test(path)
}

export function assertRelativeImagePaths(paths = []) {
  if (!Array.isArray(paths) || paths.some((path) => !isRelativeMediaPath(path))) {
    throw new Error('Зображення повинні містити лише відносні безпечні шляхи.')
  }
  return [...paths]
}

export function moveImage(paths, fromIndex, toIndex) {
  const result = [...paths]
  if (fromIndex < 0 || toIndex < 0 || fromIndex >= result.length || toIndex >= result.length) return result
  const [item] = result.splice(fromIndex, 1)
  result.splice(toIndex, 0, item)
  return result
}

export function partitionAssignments(items = [], categoryId) {
  const sorted = [...items].sort((a, b) => Number(a.sortOrder ?? 100) - Number(b.sortOrder ?? 100))
  return {
    direct: sorted.filter((item) => String(item.sourceCategoryId) === String(categoryId)),
    inherited: sorted.filter((item) => String(item.sourceCategoryId) !== String(categoryId)),
  }
}

export function entityName(entity, locale = 'uk') {
  if (!entity) return ''
  if (typeof entity.name === 'string') return entity.name
  return resolveTranslatedField(entity.translations, locale, 'name')
}

export function productCharacteristicOperations(original = [], drafts = {}, deletedIds = []) {
  const originalById = new Map(original.map((item) => [String(item.id || item.characteristicId), item.valueTranslations || item.translations || {}]))
  const deleted = new Set(deletedIds.map(String))
  const operations = []
  for (const characteristicId of deleted) {
    if (originalById.get(characteristicId)?.uk?.value) operations.push({ type: 'delete', characteristicId })
  }
  for (const [characteristicId, values] of Object.entries(drafts)) {
    if (deleted.has(characteristicId)) continue
    const uk = String(values.uk ?? '').trim()
    const en = String(values.en ?? '').trim()
    const before = originalById.get(characteristicId) || {}
    const changed = uk !== String(before?.uk?.value ?? '') || en !== String(before?.en?.value ?? '')
    if (!changed || (!uk && !before?.uk?.value)) continue
    operations.push({
      type: 'upsert',
      characteristicId,
      translations: buildValueTranslationPayload({ uk, en }),
    })
  }
  return operations
}

export function mergeCharacteristicDefinitions(definitions = [], original = []) {
  const values = new Map(original.map((item) => [String(item.id || item.characteristicId), item.valueTranslations || {}]))
  return definitions.map((definition) => {
    const id = String(definition.id || definition.characteristicId)
    return { ...definition, id, valueTranslations: values.get(id) || definition.valueTranslations || {} }
  })
}

export function reconcileCharacteristicSave(original = [], drafts = {}, deletedIds = [], succeeded = []) {
  const nextOriginal = original.map((item) => ({ ...item, valueTranslations: structuredCloneSafe(item.valueTranslations || {}) }))
  const byId = new Map(nextOriginal.map((item) => [String(item.id || item.characteristicId), item]))
  const nextDrafts = structuredCloneSafe(drafts)
  const remainingDeleted = new Set(deletedIds.map(String))
  for (const item of succeeded) {
    const operation = item.operation
    const id = String(operation.characteristicId)
    let definition = byId.get(id)
    if (!definition && operation.type === 'upsert') {
      definition = { id, characteristicId: id, valueTranslations: {} }
      nextOriginal.push(definition)
      byId.set(id, definition)
    }
    if (definition) definition.valueTranslations = operation.type === 'delete' ? {} : structuredCloneSafe(operation.translations)
    if (operation.type === 'delete') {
      nextDrafts[id] = { uk: '', en: '' }
      remainingDeleted.delete(id)
    }
  }
  return { original: nextOriginal, drafts: nextDrafts, deletedIds: [...remainingDeleted] }
}

export function createLatestRequestGate() {
  let version = 0
  return {
    begin() {
      version += 1
      return version
    },
    isCurrent(requestVersion) {
      return requestVersion === version
    },
  }
}
